import classNames from "classnames";
import { observer } from "mobx-react-lite";
import { usePostHog } from "posthog-js/react";
import React from "react";
import { useMap } from "react-map-gl/maplibre";

import { useDragging } from "../contexts/DraggingContext";
import type AircraftModel from "../model/AircraftModel";
import { setCurrentAircraftId } from "../model/CurrentAircraft";
import {
	adminStore,
	aircraftStore,
	// configurationStore,
	cwpStore,
	roleConfigurationStore,
} from "../state";
import AircraftContentSmall from "./AircraftContentSmall";
import AircraftLevelPopup from "./AircraftLevelPopup";
import AircraftPopupContent from "./AircraftPopupContent";
import ATCMenu from "./ATCMenu";
import ChangeBearingPopup from "./ChangeBearingPopup";
import ChangeNextFixPopup from "./ChangeNextFixPopup";
import ChangeSpeedPopup from "./ChangeSpeedPopup";
import Stca from "./conflict-detection-tools/Stca";
import Tct from "./conflict-detection-tools/Tct";
import DraggablePopup, { DraggablePopupProperties } from "./DraggablePopup";
import NextSectorPopup from "./NextSectorPopup";
import AddRequestButton from "./team-assistant/AddRequestButton";
import AddRequestDialog from "./team-assistant/AddRequestDialog";
import RequestPanelContainer from "./team-assistant/RequestPanelContainer";

/**
 * REQUIREMENTS:
 * 1. The popup should be positioned in the opposite direction of the aircraft's bearing
 * 2. The offset distance should scale with speed:
 *    - Minimum offset: 16 pixels (for slow/stationary aircraft)
 *    - Maximum offset: 40 pixels (for fast aircraft at ~600 knots)
 *    - Linear interpolation between min and max based on speed
 * 3. The popup must NOT overlap with a 16x16 pixel exclusion zone centered at (0,0)
 *    - This exclusion zone represents the aircraft marker
 *    - The zone extends from (-8, -8) to (8, 8)
 * 4. The popup has anchor="top", meaning:
 *    - The offset position is the top-center of the popup
 *    - The popup extends downward from this point
 *    - Horizontally, it extends from -width/2 to +width/2 from the offset position
 * 5. If the speed-based offset would cause overlap with the exclusion zone:
 *    - Calculate the minimum offset needed to clear the exclusion zone
 *    - Use this larger offset instead
 * 6. The total offset should never exceed 80 pixels, even if that means
 *    accepting some overlap with the exclusion zone in extreme cases
 *
 * @param bearing - Aircraft bearing in degrees (0 = North, 90 = East, 180 = South, 270 = West)
 * @param speed - Aircraft speed in knots
 * @param width - Popup width in pixels
 * @param height - Popup height in pixels
 * @returns Offset coordinates {x, y} in pixels
 */
function computePopupOffset(
	_bearing: number,
	_speed: number,
	_width: number,
	_height: number,
): { x: number; y: number } {
	return { x: 16, y: 16 }; // Placeholder implementation
}

export default observer(function AircraftPopup(properties: {
	aircraft: AircraftModel;
	pseudo: boolean;
}) {
	const posthog = usePostHog();
	const { aircraft } = properties;
	const {
		aircraftId,
		lastKnownLongitude: longitude,
		lastKnownLatitude: latitude,
		lastKnownAltitude: altitude,
		localAssignedFlightLevel,
		lastKnownBearing: bearing,
		lastKnownSpeed: speed,
	} = aircraft;
	const { selectedAircraftIds } = cwpStore;
	const isHoveredMarker =
		cwpStore.hoveredMarkerAircraftId === aircraftId ||
		cwpStore.hoveredConflictAircraftIds.has(aircraftId);
	const isHoveredTaLabel = cwpStore.hoveredTaLabelAircraftId === aircraftId;
	const isHoveredLabel =
		cwpStore.hoveredFlightLabelId === aircraftId || isHoveredTaLabel;
	const isSelected = selectedAircraftIds.has(aircraft.aircraftId);
	const hasOpenPopup = cwpStore.aircraftHasOpenPopup(aircraftId);

	// Use base color (without warning) for popup content
	const flightColor = roleConfigurationStore.getBaseColorOfAircraft(aircraftId);
	// Use original color (with warning) for border/icon elements
	const iconColor =
		roleConfigurationStore.getOriginalColorOfAircraft(aircraftId);

	const { current } = useMap();
	const { isDragging, isStillDragging } = useDragging();

	React.useEffect(() => {
		if (localAssignedFlightLevel === altitude.toFixed(0)) {
			aircraft.setLocalAssignedFlightLevel(" ");
		}
	}, [altitude, localAssignedFlightLevel, aircraft]);

	const onWheel = (event: React.WheelEvent): void => {
		const map = current?.getMap();
		if (map?.scrollZoom?.wheel) {
			(map.scrollZoom.wheel as (event: unknown) => void)({
				...event,
				preventDefault: () => {},
			});
		}

		posthog?.capture("aircraft_popup_wheel_scroll", {
			aircraft_id: aircraftId,
			callsign: aircraft.callSign,
			delta_y: event.deltaY,
		});
	};

	// Show expanded content when hovering OR when any popup is open
	const showExpandedContent = isHoveredLabel || hasOpenPopup;
	const defaultHeight =
		Math.round(aircraft.lastKnownAltitude) / 10 !==
			Number.parseInt(aircraft.nextSectorFL) || showExpandedContent
			? 70
			: 56;
	const defaultWidth = showExpandedContent ? 135 : 83;
	const [measuredSize, setMeasuredSize] = React.useState({
		width: defaultWidth,
		height: defaultHeight,
	});
	const popupContentRef = React.useRef<HTMLDivElement | null>(null);

	React.useEffect(() => {
		setMeasuredSize({ width: defaultWidth, height: defaultHeight });
	}, [defaultWidth, defaultHeight]);

	React.useLayoutEffect(() => {
		const element = popupContentRef.current;
		if (!element) {
			return;
		}

		const updateSize = (): void => {
			const rect = element.getBoundingClientRect();
			const nextWidth = Math.ceil(rect.width);
			const nextHeight = Math.ceil(rect.height);

			setMeasuredSize((current) =>
				current.width === nextWidth && current.height === nextHeight
					? current
					: { width: nextWidth, height: nextHeight },
			);
		};

		updateSize();

		const resizeObserver = new ResizeObserver(() => {
			updateSize();
		});
		resizeObserver.observe(element);

		return () => {
			resizeObserver.disconnect();
		};
	}, [showExpandedContent, aircraft.aircraftId]);

	const Content = showExpandedContent
		? AircraftPopupContent
		: AircraftContentSmall;

	const onClick = (): void => {
		if (isDragging) {
			return;
		}
		setCurrentAircraftId(aircraftId);

		posthog?.capture("aircraft_popup_clicked", {
			aircraft_id: aircraftId,
			callsign: aircraft.callSign,
			altitude: altitude,
			speed: speed,
			is_selected: isSelected,
		});
	};
	const onMouseEnter = (): void => {
		if (isStillDragging()) {
			return;
		}
		cwpStore.setHoveredFlightLabelId(aircraftId);
		posthog?.capture("aircraft_popup_hover_start", {
			aircraft_id: aircraftId,
			callsign: aircraft.callSign,
			is_selected: isSelected,
		});
	};
	const onMainPanelEnter = (): void => {
		if (isStillDragging()) {
			return;
		}
		cwpStore.removeHoveredTaLabelAircraftId();
		cwpStore.removeTaArrowClickedAircraftId();
	};
	const onMouseLeave = (): void => {
		if (!isDragging) {
			cwpStore.removeHoveredFlightLabelId();
			cwpStore.removeHoveredTaLabelAircraftId();
			cwpStore.removeTaArrowClickedAircraftId();

			posthog?.capture("aircraft_popup_hover_end", {
				aircraft_id: aircraftId,
				callsign: aircraft.callSign,
			});
		}
	};

	const offset = computePopupOffset(
		bearing,
		speed,
		measuredSize.width,
		measuredSize.height,
	);
	const conflictFlightId = aircraft.assignedFlightId || aircraft.aircraftId;
	const hasStcaConflict = aircraftStore.hasStcaConflict(conflictFlightId);
	const hasTctConflict = aircraftStore.hasTctConflict(conflictFlightId);

	const stcaConflictPair = hasStcaConflict
		? aircraftStore.getConflictPairAircraftIdsForFlightId(
				conflictFlightId,
				"stca",
			)
		: null;
	const tctConflictPair = hasTctConflict
		? aircraftStore.getConflictPairAircraftIdsForFlightId(
				conflictFlightId,
				"tct",
			)
		: null;

	const handleConflictBadgeMouseEnter = (
		pair: [string, string] | null,
	): void => {
		if (!pair || isStillDragging()) {
			return;
		}

		const ids = [...new Set(pair)];
		cwpStore.setHoveredConflictAircraftIds(ids);
		cwpStore.setHoveredMarkerAircraftId(ids[0]);
		for (const id of ids) {
			cwpStore.setFlightRouteForAircraft(id, true);
		}
	};

	const handleConflictBadgeMouseLeave = (
		pair: [string, string] | null,
	): void => {
		if (!pair || isStillDragging()) {
			return;
		}

		const ids = [...new Set(pair)];
		cwpStore.clearHoveredConflictAircraftIds();
		cwpStore.removeHoveredMarkerAircraftId();
		for (const id of ids) {
			if (!cwpStore.selectedAircraftIds.has(id)) {
				cwpStore.setFlightRouteForAircraft(id, false);
			}
		}
	};

	// Determine line color: selected takes priority, then hovered, then default iconColor
	const lineColor = isSelected || isHoveredMarker ? "#00FFFF" : iconColor;

	const showAddTaRequestButton =
		showExpandedContent &&
		(cwpStore.pseudoPilot || adminStore.adminModeEnabled);

	// const showAddTaRequestButton =
	// 	showExpandedContent &&
	// 	(cwpStore.pseudoPilot ||
	// 		adminStore.adminModeEnabled ||
	// 		configurationStore.currentCWP === "All");

	const showAddTaDialogOpened =
		cwpStore.aircraftsWithAddRequestDialog.has(aircraftId);

	const showRequestPanelContainer = aircraftStore.hasTeamAssistantRequests(
		properties.aircraft.callSign,
	);
	const shouldEnforceCompactConflictMinWidth =
		!showExpandedContent && hasStcaConflict && hasTctConflict;

	return (
		<DraggablePopup
			className="text-xs p-0 m-0 z-0"
			style={{ color: flightColor }}
			color={lineColor}
			offset={offset as DraggablePopupProperties["offset"]}
			size={measuredSize}
			borderRadius={1.5}
			anchor="top"
			longitude={longitude}
			latitude={latitude}
			closeOnClick={false}
			closeButton={false}
			focusAfterOpen={false}
			cancel="input, button"
			onClose={(): void => cwpStore.closeLevelPopupForAircraft(aircraftId)}
			trackingId={aircraftId}
			trackingName={aircraft.callSign}
			trackingType="aircraft_popup"
		>
			<div
				ref={popupContentRef}
				className="relative whitespace-nowrap"
				onPointerEnter={onMouseEnter}
				onPointerLeave={onMouseLeave}
			>
				<div className="flex flex-row gap-1 items-start">
					<div
						onClick={onClick}
						onPointerEnter={onMainPanelEnter}
						className={classNames(
							"p-1 select-none backdrop-blur-[1.5px]",
							isSelected
								? "bg-neutral-800/40 rounded-xs border-[0.5px] border-cyan-400"
								: "bg-neutral-800/50 rounded-sm border-0 border-transparent",
							isHoveredMarker ? "text-pink-400" : "text-white",
							shouldEnforceCompactConflictMinWidth ? "min-w-19" : "",
							showAddTaDialogOpened ? "rounded-tr-none" : "",
						)}
						onWheel={onWheel}
					>
						<Content
							flightColor={flightColor}
							aircraft={aircraft}
							width={measuredSize.width}
						/>
						{showAddTaRequestButton && <AddRequestButton aircraft={aircraft} />}
					</div>
				</div>
				{showRequestPanelContainer || showAddTaDialogOpened ? (
					<div className="absolute left-full top-0">
						{showRequestPanelContainer && (
							<RequestPanelContainer aircraft={properties.aircraft} />
						)}
						{showAddTaDialogOpened && (
							<AddRequestDialog
								aircraft={aircraft}
								onClose={() => {
									cwpStore.closeAddRequestDialogForAircraft(aircraftId);
									cwpStore.removeHoveredFlightLabelId();
								}}
							/>
						)}
					</div>
				) : null}
			</div>
			<div className="pt-0 pl-0.5">
				<AircraftLevelPopup aircraft={aircraft} />
				<ChangeNextFixPopup aircraft={aircraft} />
				<NextSectorPopup aircraft={aircraft} />
				<ChangeSpeedPopup aircraft={aircraft} />
				<ChangeBearingPopup aircraft={aircraft} />
			</div>
			<ATCMenu aircraft={properties.aircraft} />
			{hasStcaConflict || hasTctConflict ? (
				<div className="absolute bottom-full left-1 flex">
					{hasStcaConflict && (
						<Stca
							onPointerEnter={() =>
								handleConflictBadgeMouseEnter(stcaConflictPair)
							}
							onPointerLeave={() =>
								handleConflictBadgeMouseLeave(stcaConflictPair)
							}
						/>
					)}
					{hasTctConflict && (
						<Tct
							blink={hasStcaConflict}
							onPointerEnter={() =>
								handleConflictBadgeMouseEnter(tctConflictPair)
							}
							onPointerLeave={() =>
								handleConflictBadgeMouseLeave(tctConflictPair)
							}
						/>
					)}
				</div>
			) : null}
		</DraggablePopup>
	);
});
